"use client";

import React from 'react';
import { Icons } from '@/components/icons';
import { useState } from 'react'
import { determineTextColor } from '@/lib/utils';

interface CardProps {
  id: number;
  title: string;
  description: string | null;
  url: string | null;
  hexColour: string;
  primaryBackgroundColour: string;
  cardStyle: string | null;
  isPaidProduct: boolean;
  price: number | null;
  stripePriceId: string | null;
  userId: string;
  onCardClick: (productId: number) => void;
}

const getCardStyle = (style: string | null) => {
  switch (style) {
    case 'plain':
      return 'bg-white rounded-lg drop-shadow-xl';
    case 'border':
      return 'bg-white rounded-lg border border-zinc-800';
    case 'shadow':
      return 'bg-white rounded-lg shadow-[5px_5px_0px_2px_#2E2E2E]';
    case 'transparent':
      return 'bg-white bg-opacity-20 rounded-lg shadow';
    default:
      return 'bg-white rounded-lg drop-shadow-xl';
  }
};

const Card: React.FC<CardProps> = ({ id, title, description, url, hexColour, primaryBackgroundColour, cardStyle, isPaidProduct, price, stripePriceId, userId, onCardClick }) => {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const toggleDescription = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowFullDescription(!showFullDescription);
  };

  let textColour = ''
  if (cardStyle === 'transparent') {
    textColour = determineTextColor(primaryBackgroundColour);
  }

  const getDescription = () => {
    if (showFullDescription) {
      return description;
    } else if (description && description.length > 90) {
      return description.slice(0, 90) + '...';
    } else {
      return description;
    }
  };

  const handleCardClick = () => {
    onCardClick(id)
  };

  return (
    isPaidProduct && price && stripePriceId ? (
      <div
        className={`flex items-center ${getCardStyle(cardStyle)} p-4 mx-4 my-3 w-full md:w-1/2 lg:w-1/3 transition-transform duration-450 ease-in-out transform hover:scale-105 cursor-pointer`}
        onClick={handleCardClick}
      >
        {/* Card content */}
        <div className="flex-grow">
          <h3 style={{ color: textColour }} className={`text-lg font-semibold mb-1 ml-2`}>{title}</h3>
          {description && (
            <p style={{ color: textColour ? textColour : '#2D3748', whiteSpace: 'pre-line' }} className={`text-sm mb-2 ml-2`}>
              {getDescription()}
              {description && description.length > 90 && (
                <div className="mt-2">
                  <span
                    style={{ color: hexColour }}
                    className="text-blue-500 underline font-semibold cursor-pointer"
                    onClick={toggleDescription}
                  >
                    {showFullDescription ? ' Read less' : ' Read more'}
                  </span>
                </div>
              )}
            </p>
          )}
        </div>
        <div className="ml-2">
          <p style={{ color: hexColour}} className="text-sm">{`£${(price / 100).toFixed(2)}`}</p>
        </div>
        <div>
          <Icons.chevronRight style={{ color: hexColour }} />
        </div>
      </div>
    ) : url ? (
      <a
        className={`flex items-center ${getCardStyle(cardStyle)} p-4 mx-4 my-3 w-full md:w-1/2 lg:w-1/3 transition-transform duration-450 ease-in-out transform hover:scale-105`}
        href={url}
        target="_blank"
      >
        <div className="flex-grow">
          <h3 style={{ color: textColour }} className={`text-lg font-semibold mb-1 ml-2`}>{title}</h3>
          {description && (
            <p style={{ color: textColour ? textColour : '#2D3748', whiteSpace: 'pre-line' }} className={`text-sm mb-2 ml-2`}>
            {getDescription()}
            {description && description.length > 90 && (
              <div className="mt-2">
                <span
                  style={{ color: hexColour }}
                  className="text-blue-500 underline font-semibold cursor-pointer"
                  onClick={toggleDescription}
                >
                  {showFullDescription ? ' Read less' : ' Read more'}
                </span>
              </div>
            )}
          </p>
          )}
        </div>
        <div>
          <Icons.chevronRight style={{ color: hexColour }} />
        </div>
      </a>
    ) : (
      <div className={`flex items-center ${getCardStyle(cardStyle)} p-4 mx-4 my-3 w-full md:w-1/2 lg:w-1/3 transition-transform duration-450 ease-in-out transform hover:scale-105`}>
        <div className="flex-grow">
          <h3 style={{ color: textColour }} className={`text-lg font-semibold mb-1 ml-2`}>{title}</h3>
          {description && (
            <p style={{ color: textColour ? textColour : '#2D3748', whiteSpace: 'pre-line' }} className={`text-sm mb-2 ml-2`}>
            {getDescription()}
            {description && description.length > 90 && (
              <div className="mt-2">
                <span
                  className="text-blue-500 underline font-semibold cursor-pointer"
                  onClick={toggleDescription}
                >
                  {showFullDescription ? ' Read less' : ' Read more'}
                </span>
              </div>
            )}
          </p>
          )}
        </div>
        <div>
          <Icons.chevronRight />
        </div>
      </div>
    )
  );
};

export default Card;